import { getElementSize } from "Common/componentUtils/reactElementSize";
import { ZOOM_LEVEL } from "Common/dictionaries/zoomLevel";
import { INode, LabelStyleBase, Size, SvgVisual } from "yfiles";
import getZoomLevel from "../getZoomLevel";
import nodeType from "../nodeType";
import createDivWithHtmlLabel from "./createDivWithHtmlLabel";
import createHtmlLabelCache from "./createHtmlLabelCache";
const getCorrectedElementSize = (element, minSize, maxSize) => {
    const { width, height } = getElementSize(element);
    return {
        width: Math.min(Math.max(width, minSize.width), maxSize.width),
        height: Math.min(Math.max(height, minSize.height), maxSize.height)
    };
};
export default class HtmlLabelStyle extends LabelStyleBase {
    constructor(curNodeType, minSize, maxSize, font, fontSizeMap, paddingMap, renderCallback) {
        super();
        this.nodeType = curNodeType;
        this.minSize = minSize;
        this.maxSize = maxSize;
        this.font = font;
        this.fontSizeMap = fontSizeMap;
        this.paddingMap = paddingMap;
        this.renderCallback = renderCallback;
    }
    createDivWithHtmlLabel(label, zoomLevel) {
        var _a, _b;
        return createDivWithHtmlLabel(label.text, this.font.createCopy({ fontSize: this.fontSizeMap[zoomLevel] }), this.nodeType === nodeType.GROUP && ((_a = label.layout) === null || _a === void 0 ? void 0 : _a.width) && ((_b = label.layout) === null || _b === void 0 ? void 0 : _b.height)
            ? new Size(label.layout.width, label.layout.height)
            : this.maxSize, this.nodeType === nodeType.REGULAR ? "center" : "start", this.paddingMap[zoomLevel]);
    }
    createVisual(context, label) {
        const foreignObject = document.createElementNS("http://www.w3.org/2000/svg", "foreignObject");
        foreignObject.setAttribute("x", "0");
        foreignObject.setAttribute("y", "0");
        foreignObject.setAttribute("data-nodeType", this.nodeType);
        if (!label.owner || !(label.owner instanceof INode))
            return new SvgVisual(foreignObject);
        const zoomLevel = getZoomLevel(context, this.nodeType);
        const div = this.createDivWithHtmlLabel(label, zoomLevel);
        foreignObject.setAttribute("data-zoom-level", zoomLevel);
        const tag = label.owner.tag;
        if (this.nodeType === nodeType.GROUP) {
            const resultWidth = Math.max(this.minSize.width, (tag === null || tag === void 0 ? void 0 : tag.NodeWidth) || 0);
            const resultHeight = Math.max(this.minSize.height, (tag === null || tag === void 0 ? void 0 : tag.NodeHeight) || 0);
            foreignObject.setAttribute("width", `${resultWidth}`);
            foreignObject.setAttribute("height", `${resultHeight}`);
        }
        else if (zoomLevel === ZOOM_LEVEL.HIGH) {
            const { width, height } = getCorrectedElementSize(div, this.minSize, this.maxSize);
            const resultWidth = Math.max(width, (tag === null || tag === void 0 ? void 0 : tag.NodeWidth) || width + 2);
            const resultHeight = Math.max(height, (tag === null || tag === void 0 ? void 0 : tag.NodeHeight) || height + 2);
            foreignObject.setAttribute("width", `${resultWidth}`);
            foreignObject.setAttribute("height", `${resultHeight}`);
        }
        else {
            div.style.setProperty("max-width", "100%");
            div.style.setProperty("max-height", "100%");
            div.style.setProperty("width", "100%");
            div.style.setProperty("height", "100%");
            div.style.setProperty("justify-content", "stretch");
            div.style.setProperty("align-content", "stretch");
            div.style.setProperty("white-space", "nowrap");
            const resultWidth = Math.max(label.owner.layout.width - 2, this.minSize.width);
            const resultHeight = Math.max(label.owner.layout.height - 4, this.minSize.height);
            foreignObject.setAttribute("width", `${resultWidth}`);
            foreignObject.setAttribute("height", `${resultHeight}`);
        }
        foreignObject.appendChild(div);
        this.renderCallback && this.renderCallback(context.canvasComponent, label, div);
        SvgVisual.setTranslate(foreignObject, label.owner.layout.x, label.owner.layout.y);
        foreignObject["data-cache"] = createHtmlLabelCache(label, div, zoomLevel);
        return new SvgVisual(foreignObject);
    }
    updateVisual(context, oldVisual, label) {
        const foreignObject = oldVisual.svgElement;
        if (foreignObject === null)
            return this.createVisual(context, label);
        if (!label.owner || !(label.owner instanceof INode))
            return oldVisual;
        const oldCache = foreignObject["data-cache"];
        if (!(oldCache === null || oldCache === void 0 ? void 0 : oldCache.div))
            return this.createVisual(context, label);
        const newZoomLevel = getZoomLevel(context, this.nodeType);
        if (oldCache.zoomLevel !== newZoomLevel) {
            return this.createVisual(context, label);
        }
        foreignObject.setAttribute("data-zoom-level", newZoomLevel);
        const { div } = oldCache;
        if (this.nodeType === nodeType.REGULAR && oldCache.text !== label.text) {
            div.innerHTML = label.text;
            const { width, height } = getCorrectedElementSize(div, this.minSize, this.maxSize);
            foreignObject.setAttribute("width", `${Math.min(width + 4, this.maxSize.width)}`);
            foreignObject.setAttribute("height", `${height}`);
            foreignObject["data-cache"] = createHtmlLabelCache(label, div, newZoomLevel);
        }
        if (this.nodeType === nodeType.GROUP &&
            (oldCache.layout.width !== label.layout.width ||
                oldCache.layout.height !== label.layout.height)) {
            foreignObject.setAttribute("width", `${label.layout.width}`);
            foreignObject.setAttribute("height", `${label.layout.height}`);
            foreignObject["data-cache"] = createHtmlLabelCache(label, div, newZoomLevel);
        }
        if (!div.isConnected)
            foreignObject.appendChild(div);
        SvgVisual.setTranslate(foreignObject, label.owner.layout.x, label.owner.layout.y);
        return oldVisual;
    }
    getPreferredSize(label) {
        var _a, _b;
        if (this.nodeType === nodeType.REGULAR) {
            const div = this.createDivWithHtmlLabel(label, ZOOM_LEVEL.HIGH);
            const { width, height } = getCorrectedElementSize(div, this.minSize, this.maxSize);
            return new Size(Math.min(width + 4, this.maxSize.width), height);
        }
        return ((_a = label.layout) === null || _a === void 0 ? void 0 : _a.width) && ((_b = label.layout) === null || _b === void 0 ? void 0 : _b.height)
            ? new Size(label.layout.width, label.layout.height)
            : this.maxSize;
    }
}
